import { FC, useState } from 'react';
import { Tag, Pencil, Check, X, RefreshCw } from 'lucide-react';
import { dispatch } from '../../dispatch/dispatch';
import { actions } from '../../dispatch';

interface JumiaPriceData {
  id: string;
  globalPrice: number;
  salePrice: number;
  currency: string;
  variation: string;
  visible: number;
}

interface PriceWidgetProps {
  storeId: string;
  sku: string;
  token: string;
  showProfit?: boolean;            // shows estimated profit/payout row when true
  buyingRate?: number;             // group buying price — if provided, shows profit; otherwise shows payout
  jumiaFeeRate?: number;           // default 0.15 (15% max commission)
  jumiaTransactionFee?: number;    // Jumia transaction fee per order — default 6 MAD
  ourFees?: number;                // Our packaging/handling fees — default 9 MAD
}

const PriceWidget: FC<PriceWidgetProps> = ({ storeId, sku, token, showProfit = false, buyingRate, jumiaFeeRate = 0.15, jumiaTransactionFee = 6, ourFees = 9 }) => {
  const jumiaFixedFee = jumiaTransactionFee + ourFees;
  const [priceData, setPriceData] = useState<JumiaPriceData | null>(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [newPrice, setNewPrice] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  if (!storeId) return null;

  const currentPrice = priceData
    ? (priceData.salePrice > 0 ? priceData.salePrice : priceData.globalPrice)
    : null;

  // If buyingRate is known (SKU is in a group) → full profit; otherwise → payout only
  const hasBuyingRate = buyingRate !== undefined && buyingRate > 0;
  const estimatedProfit = (price: number) =>
    price * (1 - jumiaFeeRate) - jumiaFixedFee - (hasBuyingRate ? (buyingRate ?? 0) : 0);
  const profitLabel = hasBuyingRate ? 'Est. profit' : 'Est. payout';

  const profitColor = (val: number) =>
    val >= 30 ? '#059669' : val >= 10 ? '#D97706' : '#DC2626';

  const fetchPrice = async () => {
    setLoading(true);
    setError(null);
    const resp = await dispatch(actions.getJumiaPrice, {}, token, { storeId, sku });
    if (resp && !('error' in resp)) {
      setPriceData(resp);
      setNewPrice((resp.salePrice > 0 ? resp.salePrice : resp.globalPrice).toString());
    } else {
      setError(resp?.error?.message || 'Could not fetch price');
    }
    setLoading(false);
  };

  const handleUpdate = async () => {
    if (!priceData || !newPrice || isNaN(Number(newPrice))) return;
    setSaving(true);
    setError(null);
    const resp = await dispatch(
      actions.updateJumiaPrice,
      { newPrice: parseFloat(newPrice) },
      token,
      { storeId, sku }
    );
    if (resp && !('error' in resp)) {
      setPriceData(prev => prev ? { ...prev, salePrice: parseFloat(newPrice) } : prev);
      setEditing(false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 3000);
    } else {
      setError(resp?.error?.message || 'Price update failed');
    }
    setSaving(false);
  };

  // ── Not yet loaded ────────────────────────────────────────────────────────
  if (!priceData && !loading) {
    return (
      <button
        onClick={fetchPrice}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          padding: '7px 12px',
          borderRadius: 8,
          border: '1px dashed #D1D5DB',
          background: 'transparent',
          color: '#6B7280',
          fontSize: '0.72rem',
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        <Tag size={11} />
        See Jumia price
      </button>
    );
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '7px 12px', borderRadius: 8,
        border: '1px solid #E5E7EB', background: '#FAFAFA',
        color: '#9CA3AF', fontSize: '0.72rem',
      }}>
        <RefreshCw size={11} style={{ animation: 'spin 1s linear infinite' }} />
        Fetching from Jumia…
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── Loaded ────────────────────────────────────────────────────────────────
  return (
    <div style={{
      borderRadius: 8,
      border: `1px solid ${savedFlash ? '#A7F3D0' : '#E5E7EB'}`,
      background: savedFlash ? '#F0FDF4' : '#FAFAFA',
      padding: '8px 10px',
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      transition: 'border-color 0.3s, background 0.3s',
    }}>

      {/* Price row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Tag size={12} color="#F68B1E" style={{ flexShrink: 0 }} />

        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.62rem', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Jumia listing price
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 1 }}>
            <span style={{ fontSize: '1rem', fontWeight: 800, color: '#1A1D23' }}>
              {currentPrice?.toLocaleString('fr-MA', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} {priceData?.currency}
            </span>
            {savedFlash && (
              <span style={{ fontSize: '0.65rem', color: '#1D9E75', fontWeight: 600 }}>✓ Updated on Jumia</span>
            )}
          </div>
          {priceData?.variation ? (
            <div style={{ fontSize: '0.62rem', color: '#9CA3AF', marginTop: 1 }}>
              Variation: {priceData.variation}
            </div>
          ) : null}
        </div>

        {!editing && (
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            <button
              onClick={fetchPrice}
              title="Refresh price"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 26, height: 26, borderRadius: 6,
                border: '1px solid #E5E7EB', background: '#fff',
                color: '#9CA3AF', cursor: 'pointer',
              }}
            >
              <RefreshCw size={11} />
            </button>
            <button
              onClick={() => { setEditing(true); setError(null); }}
              title="Change price"
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '4px 10px', borderRadius: 6,
                border: '1px solid #F68B1E', background: '#FFF7ED',
                color: '#F68B1E', fontSize: '0.68rem', fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              <Pencil size={10} /> Change
            </button>
          </div>
        )}
      </div>

      {/* Profit / payout estimate row */}
      {showProfit && currentPrice !== null && !editing && (() => {
        const val = estimatedProfit(currentPrice);
        const isRealFees = ourFees !== 9; // 9 is the default estimate; anything else is real group fees
        const ourFeesLabel = isRealFees
          ? `${ourFees} MAD (transport+storage+packaging)`
          : `~${ourFees} MAD our fees (est.)`;
        const feesHint = `${Math.round(jumiaFeeRate * 100)}% commission − ${jumiaTransactionFee} MAD Jumia fees − ${ourFeesLabel}`;
        const hint = hasBuyingRate
          ? `price − buying price − ${feesHint}`
          : `price − ${feesHint}`;
        return (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            paddingTop: 6, borderTop: '1px solid #F3F4F6',
            fontSize: '0.68rem',
          }}>
            <span style={{ color: '#9CA3AF', fontWeight: 500 }}>
              {profitLabel}{' '}
              <span style={{ opacity: 0.6, fontSize: '0.6rem' }}>({hint})</span>
            </span>
            <span style={{
              fontWeight: 800,
              fontSize: '0.82rem',
              color: profitColor(val),
              background: `${profitColor(val)}15`,
              borderRadius: 6,
              padding: '2px 8px',
            }}>
              {val > 0 ? '+' : ''}{val.toFixed(1)} {priceData?.currency}
            </span>
          </div>
        );
      })()}

      {/* Live preview while editing */}
      {showProfit && editing && newPrice && !isNaN(Number(newPrice)) && Number(newPrice) > 0 && (() => {
        const val = estimatedProfit(Number(newPrice));
        return (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            fontSize: '0.65rem', color: '#6B7280',
          }}>
            <span>{profitLabel} at new price:</span>
            <span style={{ fontWeight: 700, color: profitColor(val) }}>
              {val > 0 ? '+' : ''}{val.toFixed(1)} {priceData?.currency}
            </span>
          </div>
        );
      })()}

      {/* Edit row */}
      {editing && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 6, borderTop: '1px solid #E5E7EB' }}>
          <div style={{ fontSize: '0.65rem', color: '#6B7280' }}>
            New price ({priceData?.currency}):
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              type="number"
              min={1}
              step={1}
              value={newPrice}
              onChange={e => setNewPrice(e.target.value)}
              autoFocus
              onKeyDown={e => e.key === 'Enter' && handleUpdate()}
              style={{
                flex: 1,
                padding: '6px 10px',
                borderRadius: 7,
                border: '1.5px solid #F68B1E',
                fontSize: '0.85rem',
                fontWeight: 700,
                color: '#1A1D23',
                background: '#fff',
                outline: 'none',
              }}
            />
            <button
              onClick={handleUpdate}
              disabled={saving}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '6px 12px', borderRadius: 7,
                border: 'none',
                background: saving ? '#9CA3AF' : '#1D9E75',
                color: '#fff', fontSize: '0.72rem', fontWeight: 700,
                cursor: saving ? 'default' : 'pointer', whiteSpace: 'nowrap',
              }}
            >
              <Check size={12} /> {saving ? 'Saving…' : 'Apply'}
            </button>
            <button
              onClick={() => { setEditing(false); setError(null); }}
              disabled={saving}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 32, borderRadius: 7,
                border: '1px solid #E5E7EB', background: '#fff',
                color: '#6B7280', cursor: 'pointer',
              }}
            >
              <X size={12} />
            </button>
          </div>
          <div style={{ fontSize: '0.62rem', color: '#9CA3AF' }}>
            Current: {currentPrice} {priceData?.currency} — press Enter or click Apply
          </div>
        </div>
      )}

      {/* Visibility status — only show when explicitly hidden */}
      {priceData?.visible === 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 5,
          padding: '4px 8px', borderRadius: 6,
          background: '#FEF2F2', border: '1px solid #FECACA',
          fontSize: '0.65rem', fontWeight: 600, color: '#DC2626',
        }}>
          ⚠ Hidden on Jumia — customers can't see this listing
        </div>
      )}

      {error && (
        <div style={{ fontSize: '0.65rem', color: '#E24B4A', marginTop: 2 }}>
          ⚠ {error}
        </div>
      )}
    </div>
  );
};

export default PriceWidget;
